import Flickity from 'flickity';

import {register} from '../vendor/theme-scripts/theme-sections';
import {trapFocus, removeTrapFocus} from '../vendor/theme-scripts/theme-a11y';
import debounce from '../util/debounce';

const selectors = {
  dot: 'data-look-dot',
  productsHolder: 'data-products-holder',
  slider: '[data-carousel]',
  buttonClose: '[data-button-close-holder]',
  slideIndex: 'data-carousel-index',
  blockId: 'data-block-id',
  focusable: 'button',
};

const classes = {
  active: 'is-active',
  expand: 'is-expanded',
};

const sections = {};

class Look {
  constructor(section) {
    this.section = section;
    this.container = this.section.container;
    this.dots = this.container.querySelectorAll(`[${selectors.dot}]`);
    this.productsHolder = this.container.querySelector(`[${selectors.productsHolder}]`);
    this.slider = this.container.querySelector(selectors.slider);
    this.buttonClose = this.container.querySelector(selectors.buttonClose);
    this.currentDot = this.container.querySelector(`[${selectors.dot}].${classes.active}`);
    this.hasDefaultOpen = Boolean(this.currentDot);

    this.init();
  }

  init() {
    this.keyEvents = (e) => this.keyboardEventShowProductHolder(e);
    this.keyCloseEvent = (e) => this.hideProductsHolder(e);
    this.clickEventsDot = (e) => this.clickEventShowProductsHolder(e);
    this.clickEventToClose = (e) => this.clickEventCloseProductsHolder(e);
    this.toggleOnResize = (e) => debounce(this.onResize(e), 200);

    this.initSlider();
    this.addEvents();
  }

  /**
   * Init slider and add event for select slide
   */
  initSlider() {
    if (!this.slider) {
      return;
    }

    this.carousel = Flickity.data(this.slider);
    this.carousel.options.wrapAround = true;
    this.carousel.options.freeScroll = false;
    this.carousel.resize();

    this.carousel.on('change', (index) => {
      this.currentDot = this.container.querySelector(`[${selectors.dot}="${index}"]`);

      this.currentDot.classList.add(classes.active);
      this.removeClassOnSiblingDots();
    });
  }

  /**
   * Add events
   */
  addEvents() {
    this.dots.forEach((dot) => {
      dot.addEventListener('click', this.clickEventsDot);
      dot.addEventListener('keyup', this.keyEvents);
    });

    document.addEventListener('keyup', this.keyCloseEvent);
    document.addEventListener('theme:resize', this.toggleOnResize);

    if (this.buttonClose) {
      this.buttonClose.addEventListener('click', this.clickEventToClose);
    }
  }

  /**
   * Show products holder
   */
  showProductsHolder() {
    const selectCellAnimate = !this.productsHolder.classList.contains(classes.expand);
    this.currentDot.classList.toggle(classes.active);
    this.productsHolder.classList.toggle(classes.expand, this.currentDot.classList.contains(classes.active));

    if (this.currentDot.classList.contains(classes.active)) {
      this.hasDefaultOpen = true;
      this.carousel.selectCell(Number(this.currentDot.getAttribute(selectors.dot)), true, selectCellAnimate);
    }

    this.removeClassOnSiblingDots();
  }

  /**
   * Hide products holder on keypress button 'escape'
   * @param {Object} e
   * @returns
   */
  hideProductsHolder(e) {
    if (e.code !== 'Escape') {
      return;
    }

    this.removeClasses();
    removeTrapFocus();
    this.currentDot.focus();
  }

  /**
   * Remove classes on productsHolder and currentDot
   */
  removeClasses() {
    this.productsHolder.classList.remove(classes.expand);
    this.currentDot.classList.remove(classes.active);
    this.hasDefaultOpen = false;
  }

  /**
   * Click event method for show products holder
   * @param {Object} e
   */
  clickEventShowProductsHolder(e) {
    this.currentDot = e.currentTarget;
    this.showProductsHolder();
  }

  /**
   * Click event method for hide products holder
   */
  clickEventCloseProductsHolder() {
    this.removeClasses();
  }

  /**
   * Keyboard event method for show products holder and active trapfocus
   * @param {Object} e
   * @returns
   */
  keyboardEventShowProductHolder(e) {
    if (e.code !== 'Enter' && e.code !== 'Space') {
      return;
    }

    setTimeout(() => {
      const firstFocus = this.productsHolder.querySelector(selectors.focusable);
      trapFocus(this.productsHolder, {elementToFocus: firstFocus});
    }, 400);
  }

  /**
   * Hide default expanded for mobile devices and show if exists for larger devices
   */
  onResize() {
    if (this.currentDot) {
      if (window.innerWidth < window.theme.sizes.medium) {
        this.currentDot.classList.remove(classes.active);
        this.productsHolder.classList.remove(classes.expand);
      } else if (this.hasDefaultOpen) {
        this.currentDot.classList.add(classes.active);
        this.productsHolder.classList.add(classes.expand);
      }
    }
  }

  /**
   * Remove events
   */
  removeEvents() {
    this.dots.forEach((dot) => {
      dot.removeEventListener('click', this.clickEventsDot);
      dot.removeEventListener('keyup', this.keyEvents);
    });

    document.removeEventListener('keyup', this.keyCloseEvent);
    document.removeEventListener('theme:resize', this.toggleOnResize);

    if (this.buttonClose) {
      this.buttonClose.removeEventListener('click', this.clickEventToClose);
    }
  }

  /**
   * Remove active class on sibling dots
   */
  removeClassOnSiblingDots() {
    for (let sibling of this.currentDot.parentNode.children) {
      if (sibling !== this.currentDot) {
        sibling.classList.remove(classes.active);
      }
    }
  }

  onUnload() {
    if (this.slider && this.carousel) {
      this.carousel.destroy();
    }

    this.removeEvents();
  }

  onBlockSelect(e) {
    if (this.slider && this.carousel) {
      const slide = this.container.querySelector(`[${selectors.blockId}="${e.detail.blockId}"]`);
      this.productsHolder.classList.add(classes.expand);
      this.carousel.selectCell(Number(slide.getAttribute(selectors.slideIndex)), true, true);
    }
  }

  onBlockDeselect() {
    if (window.innerWidth < window.theme.sizes.medium) {
      this.productsHolder.classList.remove(classes.expand);
      this.currentDot.classList.remove(classes.active);
    }
  }
}

const lookSection = {
  onLoad() {
    sections[this.id] = [];
    sections[this.id].push(new Look(this));
  },
  onUnload() {
    sections[this.id].forEach((el) => {
      if (typeof el.onUnload === 'function') {
        el.onUnload();
      }
    });
  },
  onBlockSelect(e) {
    sections[this.id].forEach((el) => {
      if (typeof el.onBlockSelect === 'function') {
        el.onBlockSelect(e);
      }
    });
  },
  onBlockDeselect(e) {
    sections[this.id].forEach((el) => {
      if (typeof el.onBlockDeselect === 'function') {
        el.onBlockDeselect(e);
      }
    });
  },
};

register('look', [lookSection]);
